import { forwardRef, useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { pluralize } from '@/lib/format';

export interface FacetOption {
  value: string;
  label: string;
  /** Distinct links carrying this value. Never a share count. */
  count?: number;
}

/** Facet values as the API lists them, as filter options; `countOf` names the count field. */
export function toFacetOptions<T extends { value: string; label: string }>(
  values: T[] | undefined,
  countOf: (value: T) => number,
): FacetOption[] {
  return (values ?? []).map((value) => ({
    value: value.value,
    label: value.label,
    count: countOf(value),
  }));
}

interface BaseProps {
  label: string;
  options: FacetOption[];
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  /** What the option counts are counts of, singular — announced to screen readers. */
  countUnit?: string;
}

function useFilteredOptions(options: FacetOption[], search: string): FacetOption[] {
  return useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle),
    );
  }, [options, search]);
}

function OptionCount({ count, unit = 'link' }: { count?: number; unit?: string }) {
  if (count === undefined) return null;
  return (
    <span className="ml-auto shrink-0 pl-2 text-xs tabular-nums text-muted-foreground">
      {count}
      <span className="sr-only"> {pluralize(count, unit)}</span>
    </span>
  );
}

function SearchBox({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <div className="relative">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={`Filter ${label.toLowerCase()}…`}
        aria-label={`Filter ${label} options`}
        className="h-8 pl-8 text-xs"
      />
    </div>
  );
}

interface TriggerButtonProps extends ButtonProps {
  label: string;
  summary: string;
  active: boolean;
}

/**
 * Must forward its ref and spread its props: `<PopoverTrigger asChild>` clones this element to
 * attach the ref, the click/keyboard handlers and `aria-expanded`. Wrapping it in a span would
 * hand all of that to a non-focusable element and break keyboard access.
 */
const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(function TriggerButton(
  { label, summary, active, className, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      className={cn(
        'h-9 w-full justify-between gap-2 font-normal',
        active && 'border-primary',
        className,
      )}
      aria-label={`${label}: ${summary}`}
      {...props}
    >
      <span className="truncate text-left">
        <span className="text-muted-foreground">{label}: </span>
        <span className={cn(active && 'font-medium')}>{summary}</span>
      </span>
      <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 opacity-60" />
    </Button>
  );
});

/**
 * The popover both filters share: trigger, search box, loading and empty states, the option list
 * and the clear button. Each filter supplies its summary and how one option renders; `close` lets
 * a single-select pick dismiss the popover.
 */
function FacetPopover({
  label,
  options,
  loading,
  emptyMessage = 'Nothing to filter by yet.',
  className,
  summary,
  active,
  onClear,
  listProps,
  renderOption,
}: Omit<BaseProps, 'countUnit'> & {
  summary: string;
  active: boolean;
  onClear: (close: () => void) => void;
  listProps?: { role: string; 'aria-label': string };
  renderOption: (option: FacetOption, close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useFilteredOptions(options, search);
  const close = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <TriggerButton label={label} summary={summary} active={active} className={className} />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        <div className="flex flex-col gap-2">
          <SearchBox value={search} onChange={setSearch} label={label} />
          {loading ? (
            <div className="flex flex-col gap-1 p-1">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-7 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              {options.length === 0 ? emptyMessage : 'No matches.'}
            </p>
          ) : (
            <ul className="max-h-64 overflow-y-auto" {...listProps}>
              {filtered.map((option) => (
                <li key={option.value}>{renderOption(option, close)}</li>
              ))}
            </ul>
          )}
          {active ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onClear(close)}>
              Clear {label.toLowerCase()}
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Multi-select facet — currently only categories, matching the repeatable `category` param. */
export function MultiFacetFilter({
  label,
  options,
  countUnit,
  selected,
  onChange,
  ...shell
}: BaseProps & { selected: string[]; onChange: (values: string[]) => void }) {
  const summary =
    selected.length === 0
      ? 'Any'
      : selected.length === 1
        ? (options.find((option) => option.value === selected[0])?.label ?? selected[0]!)
        : `${selected.length} selected`;

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value].sort(),
    );
  };

  return (
    <FacetPopover
      {...shell}
      label={label}
      options={options}
      summary={summary}
      active={selected.length > 0}
      onClear={() => onChange([])}
      listProps={{ role: 'group', 'aria-label': label }}
      renderOption={(option) => {
        const id = `facet-${label}-${option.value}`;
        return (
          <label
            htmlFor={id}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
              'hover:bg-accent focus-within:bg-accent',
            )}
          >
            <Checkbox
              id={id}
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
            />
            <span className="min-w-0 truncate">{option.label}</span>
            <OptionCount count={option.count} unit={countUnit} />
          </label>
        );
      }}
    />
  );
}

/** Single-select facet (sharer, channel, domain) — the API accepts one value for each. */
export function SingleFacetFilter({
  label,
  options,
  countUnit,
  selected,
  onChange,
  ...shell
}: BaseProps & { selected: string | null; onChange: (value: string | null) => void }) {
  const summary = selected
    ? (options.find((option) => option.value === selected)?.label ?? selected)
    : 'Any';

  return (
    <FacetPopover
      {...shell}
      label={label}
      options={options}
      summary={summary}
      active={selected !== null}
      onClear={(close) => {
        onChange(null);
        close();
      }}
      renderOption={(option, close) => {
        const isSelected = selected === option.value;
        return (
          <button
            type="button"
            aria-pressed={isSelected}
            onClick={() => {
              onChange(isSelected ? null : option.value);
              close();
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
              'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <Check
              aria-hidden="true"
              className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <span className="min-w-0 truncate">{option.label}</span>
            <OptionCount count={option.count} unit={countUnit} />
          </button>
        );
      }}
    />
  );
}
